"use client";

import { motion } from "motion/react";
import { Shield, Lock, Globe2, CreditCard, MessageCircle } from "lucide-react";
import { useLang } from "@/context/lang";

type Copy = {
  eyebrow: string;
  title: string;
  sub: string;
  badges: { icon: "shield" | "lock" | "globe" | "stripe" | "support" | "company"; title: string; sub: string }[];
  founder: string;
  founderRole: string;
  company: string;
  honest: string;
};

const ICONS = { shield: Shield, lock: Lock, globe: Globe2, stripe: CreditCard, support: MessageCircle };

const COPY: Record<string, Copy> = {
  tr: {
    eyebrow: "ŞEFFAFLIK",
    title: "Kim olduğumuz, neye güvendiğimiz",
    sub: "Reklam değil — gerçek altyapı ve sorumlu sahibi olan bir ekip.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS", sub: "Tüm trafik şifreli, sertifika otomasyonlu" },
      { icon: "lock",   title: "Stripe ödeme",   sub: "Kart bilgileri bizim sunucumuza dokunmaz" },
      { icon: "globe",  title: "CCPA + GDPR uyumlu", sub: "Kullanıcı verisi 3. taraflarla paylaşılmaz" },
      { icon: "support",title: "Canlı destek",   sub: "Tawk üzerinden gerçek insanlar yanıtlar" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Kurucu & Yazılım",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Bu sayfada gördüğünüz hiçbir rakam veya rozet sahte değil. Sözleşmeli yorum, sahte review schema, ödenmiş referans yok.",
  },
  en: {
    eyebrow: "TRANSPARENCY",
    title: "Who's behind this product",
    sub: "Real infrastructure, real owner, no marketing fluff.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS", sub: "All traffic encrypted end-to-end" },
      { icon: "lock",   title: "Stripe billing", sub: "Card data never touches our servers" },
      { icon: "globe",  title: "CCPA + GDPR aware", sub: "Your data is never sold or shared" },
      { icon: "support",title: "Live chat support", sub: "Real humans on Tawk during EU/US hours" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Founder & Engineering",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "No paid testimonials, no fabricated review schema, no rented logos on this page.",
  },
  ar: {
    eyebrow: "الشفافية",
    title: "من خلف هذا المنتج",
    sub: "بنية تحتية حقيقية ومالك مسؤول، بدون ضوضاء تسويقية.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS", sub: "كل حركة المرور مشفرة" },
      { icon: "lock",   title: "Stripe للدفع",    sub: "بيانات البطاقة لا تمر عبر خوادمنا" },
      { icon: "globe",  title: "متوافق مع CCPA + GDPR", sub: "بياناتك ليست للبيع" },
      { icon: "support",title: "دعم مباشر",       sub: "بشر حقيقيون عبر Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "المؤسس",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "لا شهادات مدفوعة ولا تقييمات مزورة في هذه الصفحة.",
  },
  es: {
    eyebrow: "TRANSPARENCIA",
    title: "Quien esta detras de este producto",
    sub: "Infraestructura real, propietario real, sin humo de marketing.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS", sub: "Todo el trafico cifrado" },
      { icon: "lock",   title: "Stripe billing", sub: "Tu tarjeta nunca toca nuestros servidores" },
      { icon: "globe",  title: "CCPA + GDPR",    sub: "Tus datos no se venden" },
      { icon: "support",title: "Soporte real",   sub: "Humanos reales por Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Fundador",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Sin testimonios pagados ni esquemas de resena falsos.",
  },
  de: {
    eyebrow: "TRANSPARENZ",
    title: "Wer hinter dem Produkt steht",
    sub: "Echte Infrastruktur, echter Betreiber, kein Marketing-Geschwafel.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS",   sub: "Ende-zu-Ende verschlüsselt" },
      { icon: "lock",   title: "Stripe Zahlung",   sub: "Kartendaten erreichen unsere Server nie" },
      { icon: "globe",  title: "CCPA + GDPR",      sub: "Daten werden nicht verkauft" },
      { icon: "support",title: "Live-Support",     sub: "Echte Menschen via Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Gründer",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Keine bezahlten Testimonials, keine erfundene Bewertungs-Schema.",
  },
  it: {
    eyebrow: "TRASPARENZA",
    title: "Chi c'e dietro questo prodotto",
    sub: "Infrastruttura reale, proprietario reale, niente fuffa di marketing.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS",  sub: "Tutto il traffico criptato" },
      { icon: "lock",   title: "Stripe",          sub: "La tua carta non passa dai nostri server" },
      { icon: "globe",  title: "CCPA + GDPR",     sub: "I tuoi dati non si vendono" },
      { icon: "support",title: "Supporto live",   sub: "Umani veri via Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Fondatore",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Niente recensioni a pagamento o schemi review falsi.",
  },
  ru: {
    eyebrow: "ПРОЗРАЧНОСТЬ",
    title: "Кто стоит за продуктом",
    sub: "Реальная инфраструктура, реальный владелец, без маркетингового шума.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS",  sub: "Весь трафик зашифрован" },
      { icon: "lock",   title: "Stripe оплата",   sub: "Данные карты не касаются наших серверов" },
      { icon: "globe",  title: "CCPA + GDPR",     sub: "Данные не продаются" },
      { icon: "support",title: "Живая поддержка", sub: "Реальные люди через Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Основатель",
    company: "CreateNano LLC · Albuquerque, NM, США",
    honest: "Никаких платных отзывов и поддельных рейтинговых схем.",
  },
  fr: {
    eyebrow: "TRANSPARENCE",
    title: "Qui se cache derriere ce produit",
    sub: "Infrastructure reelle, proprietaire reel, sans baratin marketing.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS",  sub: "Tout le trafic chiffre" },
      { icon: "lock",   title: "Paiement Stripe", sub: "La carte n'arrive jamais a nos serveurs" },
      { icon: "globe",  title: "CCPA + GDPR",     sub: "Vos donnees ne sont pas vendues" },
      { icon: "support",title: "Support live",    sub: "De vrais humains via Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Fondateur",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Aucun temoignage paye ni schema de note fabrique.",
  },
  pt: {
    eyebrow: "TRANSPARÊNCIA",
    title: "Quem esta por tras do produto",
    sub: "Infraestrutura real, dono real, sem marketing vazio.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS",  sub: "Todo o trafego criptografado" },
      { icon: "lock",   title: "Stripe",          sub: "Cartao nao toca nossos servidores" },
      { icon: "globe",  title: "CCPA + GDPR",     sub: "Dados nao sao vendidos" },
      { icon: "support",title: "Suporte ao vivo", sub: "Humanos reais via Tawk" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Fundador",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Sem depoimentos pagos ou avaliacoes falsas.",
  },
  bs: {
    eyebrow: "TRANSPARENTNOST",
    title: "Ko stoji iza ovog proizvoda",
    sub: "Stvarna infrastruktura, stvarni vlasnik, bez marketinske buke.",
    badges: [
      { icon: "shield", title: "TLS 1.3 + HSTS",  sub: "Sav saobracaj kriptovan" },
      { icon: "lock",   title: "Stripe placanje", sub: "Vasi podaci ne dotaknu nase servere" },
      { icon: "globe",  title: "CCPA + GDPR",     sub: "Podaci se ne prodaju" },
      { icon: "support",title: "Podrska uzivo",   sub: "Pravi ljudi preko Tawk-a" },
    ],
    founder: "Göktuğ Düşünen",
    founderRole: "Osnivac",
    company: "CreateNano LLC · Albuquerque, NM, USA",
    honest: "Bez placenih svjedocenja ili izmisljenih ocjena.",
  },
};

export function Trust() {
  const { lang } = useLang();
  const t = COPY[lang as keyof typeof COPY] || COPY.en;
  const isRtl = lang === "ar";

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-950 relative overflow-hidden">
      {/* Subtle grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.025] dark:opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.4) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="max-w-6xl mx-auto relative" dir={isRtl ? "rtl" : "ltr"}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="inline-block text-[10px] font-black tracking-[0.25em] text-cyan-600 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/10 px-3 py-1.5 rounded-full mb-5">
            {t.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-neutral-900 dark:text-white mb-3">
            {t.title}
          </h2>
          <p className="text-sm sm:text-base text-neutral-600 dark:text-slate-400 leading-relaxed">
            {t.sub}
          </p>
        </motion.div>

        {/* Trust badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {t.badges.map((b, i) => {
            const Icon = ICONS[b.icon];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="group flex flex-col items-start gap-3 p-5 rounded-2xl bg-neutral-50 dark:bg-slate-900/60 border border-neutral-200 dark:border-white/10 hover:border-cyan-300 dark:hover:border-cyan-400/40 hover:shadow-lg hover:shadow-cyan-500/10 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-500/15 border border-cyan-200/60 dark:border-cyan-400/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Icon size={18} className="text-cyan-600 dark:text-cyan-300" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-sm font-black text-neutral-900 dark:text-white mb-1">{b.title}</div>
                  <div className="text-xs text-neutral-500 dark:text-slate-400 font-medium leading-snug">{b.sub}</div>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
